import { DOCUMENT, isPlatformBrowser, UpperCasePipe, CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnDestroy, OnInit, PLATFORM_ID, Renderer2, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QRCodeComponent, QRCodeElementType } from 'angularx-qrcode';
import Swal from 'sweetalert2';
import { environment } from '@env/environment';
import { IsLoggedIn } from '@core/entity/auth/state/auth.selectors';
import { CheckBannerLoadedService } from '@core/services/check-banner-loaded.service';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { svgIconList } from '@shared/svg-icon-list';
import { SignupModalComponent } from '@shared/signup-modal/signup-modal.component';
import { PopupReferralComponent } from '@shared/popup-referral/popup-referral.component';
import { MenuBarHttpService } from '@core/entity/menubar/service/menu-bar.http.service';
import { ReferralDataService } from './services/referral-data.service';

@Component({
  selector: 'app-referral',
  imports: [TranslateModule, MatDialogModule, MatTooltipModule, QRCodeComponent, SafeHtmlPipe, UpperCasePipe, CommonModule],
  templateUrl: './referral.component.html',
  styleUrl: './referral.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralComponent implements OnInit, AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private referralDataService = inject(ReferralDataService);
  private store = inject(Store);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);
  private checkBannerLoadedService = inject(CheckBannerLoadedService);
  private menuBarHttpService = inject(MenuBarHttpService);
  private el = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);

  private isBrowser = isPlatformBrowser(this.platformId);

  // Static config
  siteName = environment.siteName;
  sitePrefix = environment.sitePrefix;
  projectCode = environment.projectCode.toLocaleLowerCase();
  domainEnv = environment.domainEnv;
  isQP20 = ['qp20', 'qp20-dark'].includes(environment.projectCode.toLowerCase());
  isQP2D = ['qp2d'].includes(environment.projectCode.toLowerCase());
  svg: any = svgIconList;
  elementType = 'canvas' as QRCodeElementType;
  bannerClass = 'subpage-slider';

  // Reactive state (signals)
  isLoggedIn = toSignal(this.store.select(IsLoggedIn), { initialValue: false });
  countryCode = signal<string | undefined>(undefined);
  languageCode = signal<string | undefined>(undefined);
  userData = signal<any>(null);
  activeGameCategoryTab = signal<any>(null);
  referralData = signal<any>(null);
  isSkeletonReferralData = signal(true);
  details = signal<any>(null);
  isSkeletonDetails = signal(true);
  loading = signal(false);
  bannerLoaded = signal(false);

  // Signal-based view queries
  private tooltipCode = viewChild('tooltipCode', { read: MatTooltip });
  private tooltipLink = viewChild('tooltipLink', { read: MatTooltip });

  ngOnInit(): void {
    /* Same treatment as contact-us: tag <body> on server and browser alike so the
       scss override drops base-inner's default-height (100vh) from the first paint.
       template-02 keeps '/referral' in base-inner's isContentPage exclude list; here
       the exclude list never matches because qply's router.url carries the '/:locale'
       segment. Without this the 100vh floor plus the pinned footer leave a large gap
       between the content and the footer, growing as the browser zooms out. */
    this.renderer.addClass(this.document.body, 'referral-loaded');

    if (this.isBrowser) {
      this.countryCode.set(localStorage.getItem('country_code')?.toUpperCase());
      this.languageCode.set(localStorage.getItem('language_code')?.toLowerCase());
      const memberProfile = localStorage.getItem('memberProfile');
      this.userData.set(memberProfile ? JSON.parse(memberProfile) : null);
    }

    this.loadReferralContent();

    this.store.select(IsLoggedIn)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (!res) return;
        this.loading.set(true);
        setTimeout(() => {
          this.referralDataService.getDetails()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (details) => {
                this.loading.set(false);
                this.details.set(details);
                this.isSkeletonDetails.set(false);
              },
              error: (err) => {
                this.loading.set(false);
                Swal.fire({
                  html: '<div class="alert-svg">' + this.svg.alertIcon + '</div>' +
                    '<div class="text-center m-t-20">' + err + '</div>',
                  showCloseButton: true,
                }).then(() => this.router.navigateByUrl('/member/profile'));
              },
            });

          if (this.isBrowser) {
            this.countryCode.set(localStorage.getItem('country_code')?.toUpperCase());
            this.languageCode.set(localStorage.getItem('language_code')?.toLowerCase());
          }
        }, 500);
      });

    this.setBannerDetail();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    this.checkBannerLoadedService.getBannerLoaded(this.el.nativeElement, this.bannerClass);
    this.checkBannerLoadedService.bannerLoaded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(loaded => this.bannerLoaded.set(loaded));
  }

  ngOnDestroy(): void {
    /* Leaving the page restores default-height to its normal 100vh everywhere else. */
    this.renderer.removeClass(this.document.body, 'referral-loaded');
  }

  private setBannerDetail(): void {
    if (!this.isBrowser) return;

    // Title reflects the CMS menu item matching the current URL (e.g. "REFERRAL TEST"). Read the
    // cached menu first so it's instant on reloads, then refresh from the API — the topbar writes
    // `topbar_menu` only after its own async menu load, which lands after this component has already
    // rendered, so relying on the cache alone made the title update only on the *second* reload.
    const currentUrl = this.router.url.toLowerCase();
    const applyMenu = (menu: any[]): void => {
      const active = (menu ?? []).find((i: any) => i?.link && currentUrl.includes(i.link.toLowerCase()));
      if (active) this.activeGameCategoryTab.set(active);
    };

    try {
      applyMenu(JSON.parse(localStorage.getItem('topbar_menu') ?? '[]') || []);
    } catch { /* ignore malformed cache */ }

    this.menuBarHttpService.getMenuBar()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(menu => applyMenu(menu));
  }

  onCopyText(inputElement: any, type: string): void {
    inputElement.select();
    document.execCommand('copy');

    const tooltip = type === 'code' ? this.tooltipCode() : this.tooltipLink();
    tooltip?.show();
    setTimeout(() => tooltip?.hide(), 2000);
  }

  onShare(): void {
    if (!this.isBrowser) return;
    const details = this.details();

    if (navigator.share) {
      navigator.share({
        title: this.translateService.instant('[site_prefix]', { site_prefix: this.siteName }),
        text: this.translateService.instant('Sign up at [site_prefix] using my referral code and get [currency] [reward_amount] when you deposit more than [currency] [required_amount]!\nLink: [referral_URL]', {
          site_prefix: this.siteName,
          currency: details.currency === 'IDR' ? 'Rp' : details.currency,
          reward_amount: details.currency === 'IDR' ? (details.reward_amount * 1000).toFixed(2) : details.reward_amount,
          required_amount: details.currency === 'IDR' ? (details.required_amount * 1000).toFixed(2) : details.required_amount,
          referral_URL: details.register_link
        })
      });
    } else {
      this.dialog.open(PopupReferralComponent, { data: { details } });
    }
  }

  onJoinNow(): void {
    this.dialog.open(SignupModalComponent);
  }

  onNavigateTnC(): void {
    if (this.isBrowser) {
      sessionStorage.setItem('fromReferral', 'true');
    }
    this.router.navigateByUrl('/promotion?code=REF2104');
  }

  private loadReferralContent(): void {
    // Load on the browser only. This is a /api/member/ call that is deliberately excluded from the
    // SSR transfer cache, so if it resolved during SSR the server would render the real content
    // while the client re-renders the skeleton — an unrecoverable hydration mismatch (NG0500) that
    // aborts bootstrap and bounces the URL to '/'. Keeping the server render at the skeleton (same
    // as the client's first paint) mirrors how the other subpages (e.g. VIP) hydrate cleanly.
    if (!this.isBrowser) return;

    this.referralDataService.getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.referralData.set(res.length > 0 ? res[0]['contents'][0] : false);
        this.isSkeletonReferralData.set(false);
      });
  }

  getQRSignupLink(refcode: any): string {
    return window.location.origin + '/signup?ref=' + refcode;
  }

  onDownloadQR(parent: any): void {
    let parentElement = null;

    if (this.elementType === 'canvas') {
      parentElement = parent.qrcElement.nativeElement.querySelector('canvas').toDataURL('image/png');
    } else if (this.elementType === 'img' || this.elementType === 'url') {
      parentElement = parent.qrcElement.nativeElement.querySelector('img').src;
    } else {
      alert("Set elementType to 'canvas', 'img' or 'url'.");
    }

    if (parentElement) {
      const blobData = this.convertBase64ToBlob(parentElement);
      const blob = new Blob([blobData], { type: 'image/png' });
      try {
        if (typeof window.URL.createObjectURL === 'function') {
          const url = window.URL.createObjectURL(blob);
          this.triggerDownload(url, 'angularx-qrcode');
        } else {
          const reader = new FileReader();
          reader.onloadend = () => this.triggerDownload(reader.result as string, 'angularx-qrcode');
          reader.readAsDataURL(blob);
        }
      } catch (error) {
        console.log('Failed to create object URL:', error);
      }
    }
  }

  triggerDownload(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  }

  private convertBase64ToBlob(Base64Image: string): Blob {
    const parts = Base64Image.split(';base64,');
    const imageType = parts[0].split(':')[1];
    const decodedData = window.atob(parts[1]);
    const uInt8Array = new Uint8Array(decodedData.length);
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }
    return new Blob([uInt8Array], { type: imageType });
  }
}
